import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Search } from "lucide-react";
import { PatkolasPanel } from "./BerlesTab";
import { getAuthErrorMessage, loginInputClass } from "../LoginForm";
import { primaryButtonClass, saveButtonClass } from "./ProfilTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => localStorage.getItem("comp_token");
const GENDERS = [{ id: "kanca", name: "Kanca" }, { id: "herelt", name: "Herélt" }, { id: "men", name: "Mén" }];
const genderLabel = (g) => GENDERS.find((x) => x.id === g || x.name.toLowerCase() === (g || "").toLowerCase())?.name || g || "—";
const birth = (h) => [h.birth_year, h.birth_month, h.birth_day].filter(Boolean).map((n, i) => (i ? String(n).padStart(2, "0") : n)).join(".") || "—";
const EMPTY = { name: "", gender: "", hun_id: "", breed: "", color: "", birth_year: "", birth_month: "", birth_day: "", owner_id: "" };

export function LovakTab() {
  const [horses, setHorses] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | {} (new) | horse

  const load = async () => {
    try {
      const [h, u] = await Promise.all([
        axios.get(`${API}/competition/horses`),
        axios.get(`${API}/competition/users`, { params: { token: token() } }),
      ]);
      setHorses(h.data || []);
      setUsers(u.data || []);
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
      setHorses([]);
    }
  };
  useEffect(() => { load(); }, []);

  const byId = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const list = useMemo(() => {
    const n = q.trim().toLowerCase();
    return [...(horses || [])]
      .filter((h) => !n || [h.name, h.hun_id, h.owner, byId[h.owner_id]?.identifier].some((v) => (v || "").toLowerCase().includes(n)))
      .sort((a, b) => (a.name || "").localeCompare(b.name || "", "hu"));
  }, [horses, q, byId]);

  const remove = async (h) => {
    if (!window.confirm(`Biztosan törlöd a(z) ${h.name} lovat?`)) return;
    try {
      await axios.delete(`${API}/competition/horses/${h.id}`, { params: { token: token() } });
      toast.success("Ló törölve");
      load();
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
    }
  };

  return (
    <div className="mx-auto w-full max-w-4xl rounded-3xl bg-white p-5 sm:p-7 shadow-[0_10px_40px_rgba(15,23,42,0.08)] ring-1 ring-slate-100" data-testid="lovak-tab">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-slate-900">Lovak {horses && <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-700" data-testid="horses-count">{horses.length} ló</span>}</h2>
        <button type="button" onClick={() => setEditing({ ...EMPTY })} className={primaryButtonClass} data-testid="add-horse-btn"><Plus size={16} /> Ló hozzáadása</button>
      </div>
      <div className="relative mb-4">
        <Search size={18} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-600" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Keresés név, azonosító vagy tulajdonos alapján" className={`${loginInputClass} pl-11`} data-testid="horses-search-input" />
      </div>
      {!horses ? <p className="py-8 text-center text-slate-600">Betöltés...</p> : list.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-600" data-testid="horses-empty">Nincs találat.</p>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="horses-list">
          {list.map((h) => {
            const owner = byId[h.owner_id];
            return (
              <li key={h.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center" data-testid={`horse-row-${h.id}`}>
                <div className="min-w-0 flex-1 text-sm">
                  <p className="font-semibold text-slate-800">{h.name} {h.hun_id && <span className="font-mono text-xs font-medium text-slate-700">({h.hun_id})</span>}</p>
                  <p className="text-xs text-slate-700">
                    {genderLabel(h.gender)}{h.breed ? ` · ${h.breed}` : ""}{h.color ? ` · ${h.color}` : ""} · Szül.: {birth(h)}
                    {" · "}Tulajdonos: <span className="font-medium text-slate-700">{owner?.name || h.owner || "—"}</span>{owner?.identifier ? <span className="font-mono"> ({owner.identifier})</span> : null}
                  </p>
                  <PatkolasPanel horse={h} />
                </div>
                <div className="flex shrink-0 gap-2">
                  <button type="button" onClick={() => setEditing({ ...EMPTY, ...h })} className="inline-flex items-center gap-1 rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100" data-testid={`edit-horse-${h.id}`}><Pencil size={14} /> Szerkesztés</button>
                  <button type="button" onClick={() => remove(h)} className="inline-flex items-center gap-1 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100" data-testid={`delete-horse-${h.id}`}><Trash2 size={14} /> Törlés</button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
      {editing && <HorseModal horse={editing} users={users} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function HorseModal({ horse, users, onClose, onDone }) {
  const [form, setForm] = useState(horse);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const isNew = !horse.id;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("A ló neve kötelező"); return; }
    setBusy(true);
    const body = { name: form.name, gender: form.gender, hun_id: form.hun_id, breed: form.breed, color: form.color,
      birth_year: parseInt(form.birth_year) || 0, birth_month: parseInt(form.birth_month) || 0, birth_day: parseInt(form.birth_day) || 0, owner_id: form.owner_id || "" };
    try {
      if (isNew) await axios.post(`${API}/competition/horses`, body, { params: { token: token() } });
      else await axios.put(`${API}/competition/horses/${horse.id}`, body, { params: { token: token() } });
      toast.success(isNew ? "Ló hozzáadva" : "Ló adatai mentve");
      onDone();
    } catch (err) {
      toast.error(getAuthErrorMessage(err));
    }
    setBusy(false);
  };

  const sorted = [...users].sort((a, b) => (a.name || "").localeCompare(b.name || "", "hu"));
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 p-4 backdrop-blur-sm" data-testid="horse-modal">
      <form onSubmit={submit} className="max-h-[90vh] w-full max-w-md space-y-3 overflow-y-auto rounded-3xl bg-white p-6 shadow-2xl sm:p-8">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-slate-900">{isNew ? "Ló hozzáadása" : "Ló szerkesztése"}</h3>
          <button type="button" onClick={onClose} className="rounded-lg p-1 text-slate-600 hover:bg-slate-100" data-testid="horse-modal-close"><X size={22} /></button>
        </div>
        <input value={form.name} onChange={set("name")} placeholder="Név *" className={loginInputClass} data-testid="horse-name-input" />
        <select value={form.gender} onChange={set("gender")} className={loginInputClass} data-testid="horse-gender-select">
          <option value="">Nem...</option>
          {GENDERS.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
        <input value={form.hun_id} onChange={set("hun_id")} placeholder="Azonosító (HUN)" className={loginInputClass} data-testid="horse-hunid-input" />
        <input value={form.breed} onChange={set("breed")} placeholder="Fajta" className={loginInputClass} data-testid="horse-breed-input" />
        <input value={form.color} onChange={set("color")} placeholder="Szín" className={loginInputClass} data-testid="horse-color-input" />
        <div className="grid grid-cols-3 gap-2">
          <input type="number" value={form.birth_year || ""} onChange={set("birth_year")} placeholder="Év" className={loginInputClass} data-testid="horse-year-input" />
          <input type="number" value={form.birth_month || ""} onChange={set("birth_month")} placeholder="Hó" className={loginInputClass} data-testid="horse-month-input" />
          <input type="number" value={form.birth_day || ""} onChange={set("birth_day")} placeholder="Nap" className={loginInputClass} data-testid="horse-day-input" />
        </div>
        <select value={form.owner_id} onChange={set("owner_id")} className={loginInputClass} data-testid="horse-owner-select">
          <option value="">Tulajdonos...</option>
          {sorted.map((u) => <option key={u.id} value={u.id}>{u.name}{u.identifier ? ` (${u.identifier})` : ""}</option>)}
        </select>
        <button type="submit" disabled={busy} className={saveButtonClass} data-testid="horse-save-btn">{busy ? "Mentés..." : "Mentés"}</button>
      </form>
    </div>
  );
}

export default LovakTab;
